import type { PageEntry, Entry, Node } from 'contensis-delivery-api';
import type { PagedList } from 'contensis-core-api';
import cmsParser from 'Common/util/cmsParser';

import type { Props } from './Listing';
import { sanitizePhoneNumber } from 'Common/util/string';

export type PageData = {
    entry: PageEntry;
    ancestors: Node[];
    profiles: PagedList<Entry>;
    searchTerm: string;
    roleValue: string;
    departmentValue: string;
};

const datamap = (data: PageData): Props => {
    const { entry, ancestors, profiles, searchTerm, roleValue, departmentValue } = data;

    return {
        breadcrumbs: cmsParser.breadcrumbs([
            ...ancestors,
            { entry: { title: entry.title }, id: entry.sys.id }
        ]),
        heroBanner: cmsParser.heroBannerWave(entry.heroBannerWaveDefault),
        staffFilters: {
            id: 'staff_role_filter',
            initialSearchTerm: searchTerm,
            initialRoles: roleValue,
            initialDepartments: departmentValue,
            rolesSelectOptions: entry.filters[0].options.map(item => ({
                id: item.key,
                label: item.name,
                value: item.key.split('/').join('-')
            })),
            departmentsSelectOptions: entry.filters[1].options
                .map(item => ({
                    id: item.key,
                    label: item.name,
                    value: item.key.split('/').join('-')
                }))
                .sort((a: { label: number }, b: { label: number }) => (a.label < b.label ? -1 : 1))
        },
        staffProfiles: {
            items: profiles.items.map(profile => ({
                id: profile.sys.id,
                honorific: profile.staffMember.honorific?.name,
                firstName: profile.staffMember.person.forename,
                lastName: profile.staffMember.person.surname,
                jobTitle: profile.staffMember.jobTitle,
                info: profile.department.name,
                email:
                    profile.staffMember.person.email && !profile.staffMember.person.hideEmail
                        ? {
                              label: profile.staffMember.person.email,
                              ariaLabel: `Email ${profile.staffMember.person.email}`,
                              href: `mailto:${profile.staffMember.person.email}`
                          }
                        : undefined,
                phone: profile.staffMember.person.phoneNumber
                    ? {
                          label: profile.staffMember.person.phoneNumber,
                          ariaLabel: `Call ${profile.staffMember.person.phoneNumber}`,
                          href: `tel:${sanitizePhoneNumber(profile.staffMember.person.phoneNumber)}`
                      }
                    : undefined,
                profileLink: cmsParser.link({
                    ...profile,
                    label: `View profile`,
                    ariaLabel: `Click to view profile`
                }),
                image: profile.staffMember.person.picture
                    ? {
                          ...cmsParser.image(profile.staffMember.person.picture)
                      }
                    : undefined
            })),
            pagination: {
                id: 'staffprofile-listing-pagination',
                totalCount: profiles.totalCount,
                pageSize: profiles.pageSize,
                initialPageIndex: profiles.pageIndex
            }
        }
    };
};

export default datamap;
